<template>
    <!-- ⚠️ ตัว <id-modal> teleport ไป <body> และทุกตัวใช้ z-index 2500 เท่ากัน — ตัวที่อยู่
         "หลังสุดใน DOM" เท่านั้นที่ชนะ · กล่องนี้ต้องซ้อนอยู่บนฟอร์มที่เปิดค้างอยู่เสมอ
         พ่อแม่จึงต้องวาง <duplicate-student-dialog> ไว้ **หลัง** id-modal ตัวอื่นทุกตัวในไฟล์ -->
    <id-modal :model-value="modelValue" @update:model-value="$emit('update:modelValue', $event)"
        size="lg" icon="mdi-account-alert-outline" :title="$t('dup.title')" :subtitle="$t('dup.subtitle')" persistent>
        <div class="dup-intro">{{ $t('dup.intro', { n: candidates.length }) }}</div>

        <!-- ปุ่มย้ายต้องมีครอบครัวปลายทาง — ถ้ายังไม่ได้เลือก Username ผปค. ในฟอร์ม
             ปุ่มจะจางและกดไม่ได้ ต้องบอกเหตุผล ไม่ใช่ปล่อยให้เดาเอง -->
        <div v-if="!targetFamilyId" class="dup-need-fam">
            <span class="mdi mdi-information-outline"></span> {{ $t('dup.pickFamilyFirst') }}
        </div>

        <div v-for="c in candidates" :key="c.studentid" class="dup-card">
            <div class="dup-card-top">
                <div>
                    <div class="dup-name">{{ c.fullname }}<span v-if="c.nickname" class="dup-nick">({{ c.nickname }})</span></div>
                    <div class="dup-why"><span class="dup-conf" :class="'cf-' + c.confidence">{{ $t('dup.conf.' + c.confidence) }}</span>
                        {{ $t('dup.why.' + c.matchKey) }}</div>
                </div>
                <button class="id-btn id-btn-primary id-btn-sm" :disabled="busy || !targetFamilyId"
                    @click="$emit('move', c)"><span class="mdi mdi-account-arrow-right-outline"></span> {{ $t('dup.moveHere') }}</button>
            </div>
            <div class="dup-meta">
                <span><span class="mdi mdi-home-outline"></span>
                    {{ c.family_username || $t('dup.noFamily') }}<template v-if="c.staffFamily"> · {{ $t('dup.staffRow') }}</template></span>
                <span><span class="mdi mdi-cake-variant-outline"></span> {{ c.dateofbirth ? $formatShortDate(c.dateofbirth) : '—' }}</span>
                <span><span class="mdi mdi-calendar-check-outline"></span> {{ $t('dup.bookings', { n: c.bookings }) }}</span>
                <span><span class="mdi mdi-history"></span>
                    {{ c.lastclassdate ? $t('dup.lastClass', { d: $formatShortDate(c.lastclassdate) }) : $t('dup.noClass') }}</span>
                <span v-if="c.courserefer"><span class="mdi mdi-ticket-confirmation-outline"></span> {{ c.courserefer }}</span>
            </div>
        </div>

        <!-- ประตูที่สอง: สร้างเด็กเองตรงนี้ต้องผ่านด่านเดียวกับฝั่งอนุมัติใบรออนุมัติ
             ไม่งั้นแอดมินที่ถูกล็อกฝั่งโน้นเดินมาสร้างเองตรงนี้ได้ โดยไม่เหลือร่องรอยเลย -->
        <div v-if="headOnlyBlocked" class="dup-lock">
            <span class="mdi mdi-lock-outline"></span> {{ $t('dup.headOnlyCreateHint') }}
        </div>
        <div v-else class="dup-reason">
            <div class="modal-sec mt"><span class="mdi mdi-comment-alert-outline"></span> {{ $t('dup.reasonTitle') }}</div>
            <div class="dup-intro" style="margin-bottom:6px">{{ $t('dup.reasonCreateHint') }}</div>
            <textarea class="id-input id-textarea" rows="2" maxlength="500"
                v-model="reason" :placeholder="$t('dup.reasonPlaceholder')"></textarea>
        </div>

        <template #footer>
            <button class="id-btn id-btn-ghost" @click="$emit('update:modelValue', false)">{{ $t('btn.cancel') }}</button>
            <button class="id-btn id-btn-primary" :disabled="busy || headOnlyBlocked || !reason.trim()"
                @click="$emit('create-anyway', reason.trim())">
                <span class="mdi mdi-account-plus-outline"></span> {{ $t('dup.createAnyway') }}</button>
        </template>
    </id-modal>
</template>

<script>
// กล่อง "อาจเป็นเด็กคนเดียวกัน" — ใช้ร่วมกัน 2 จอที่สร้างเด็กได้ (รายชื่อนักเรียน / จัดการบัญชี)
// เป็น presentational ล้วน พ่อแม่เป็นคนยิง API เอง
// ทิศทางเดียว: ย้าย "แถวเดิมที่เจอ" เข้า targetFamilyId ที่แอดมินกำลังกรอกอยู่
// (แท็บ "ยังไม่มีผู้ปกครอง" ไม่ได้ใช้กล่องนี้ — ที่นั่นการจับคู่ได้แปลว่า "ต้องรวมประวัติ" ไม่ใช่ "ย้าย")
export default {
    name: 'DuplicateStudentDialog',
    props: {
        modelValue: { type: Boolean, default: false },
        candidates: { type: Array, default: () => [] },
        targetFamilyId: { type: [Number, String], default: null },
        busy: { type: Boolean, default: false },
    },
    emits: ['update:modelValue', 'move', 'create-anyway'],
    data() {
        return { reason: '' };
    },
    computed: {
        // ⚠️ อ่านสิทธิ์ไม่ได้ = ไม่ล็อก แล้วปล่อยให้ backend ตัดสิน — ด่านฝั่งจอมีไว้บอกล่วงหน้า
        // ห้ามเป็นตัวที่ปฏิเสธคนผิด (เส้น ForgotPassword ไม่ได้เก็บ userdata ลง store)
        headOnlyBlocked() {
            const t = (this.$store.getters.getUserdata || {}).usertype;
            if (t === undefined || t === null || t === '') return false;
            const top = (this.candidates || [])[0];
            // veryhigh + high = ผู้จัดการเท่านั้น (เจ้าของเคาะ 2026-10-16) · ตรงกับ DUP_HEAD_ONLY_CONFIDENCE ฝั่ง backend
            return !!top && ['veryhigh', 'high'].includes(top.confidence) && String(t) !== '0';
        },
    },
    watch: {
        // เปิดกล่องใหม่ทุกครั้งต้องเริ่มจากช่องว่าง ไม่ใช่เหตุผลของเด็กคนก่อน
        modelValue(v) { if (v) this.reason = ''; },
    },
};
</script>

<style scoped>
/* ชื่อคลาส .dup-* ใช้เฉพาะที่นี่ (เช็คแล้วไม่ชนกับ istar-pages.css / global-style.css) */
.dup-intro { margin-bottom: 12px; color: var(--c-text-muted); font-size: 13.5px; line-height: 1.6; }
.dup-need-fam {
  display: flex; align-items: center; gap: 6px; margin-bottom: 10px; padding: 8px 11px;
  border-radius: 9px; font-size: 12.5px; color: var(--c-text-muted);
  background: color-mix(in srgb, var(--c-warning) 10%, transparent);
  border: 1px solid color-mix(in srgb, var(--c-warning) 40%, transparent);
}
/* แถบล็อก — เข้มกว่ากล่องเตือนรอบนอกเพราะเป็นข้อจำกัดจริง ไม่ใช่คำแนะนำ */
.dup-lock {
  display: flex; align-items: center; gap: 7px; margin-top: 12px; padding: 9px 12px;
  border-radius: 9px; font-size: 13px; font-weight: 600; color: var(--c-text-heading);
  background: color-mix(in srgb, var(--c-error) 12%, transparent);
  border: 1px solid color-mix(in srgb, var(--c-error) 40%, transparent);
}
.dup-lock .mdi { color: var(--c-error); font-size: 17px; }
.dup-reason { margin-top: 4px; }
.dup-card {
  border: 1px solid var(--c-border); border-radius: 12px; padding: 12px 14px; margin-bottom: 10px;
  background: var(--c-surface-2);
}
.dup-card-top { display: flex; justify-content: space-between; align-items: flex-start; gap: 12px; }
.dup-name { font-family: var(--font-head); font-weight: 700; color: var(--c-text-heading); }
.dup-nick { color: var(--c-text-muted); margin-left: 5px; }
.dup-why { margin-top: 3px; font-size: 12.5px; color: var(--c-text-muted); }
.dup-conf {
  display: inline-block; margin-right: 6px; padding: 1px 8px; border-radius: 999px;
  font-size: 11.5px; font-weight: 700; color: #fff; background: var(--c-info);
}
.dup-conf.cf-veryhigh { background: var(--c-error); }
.dup-conf.cf-high { background: var(--c-warning); }
.dup-meta {
  display: flex; flex-wrap: wrap; gap: 6px 16px; margin-top: 9px;
  font-size: 12.5px; color: var(--c-text-muted);
}
.dup-meta .mdi { margin-right: 3px; }
@media (max-width: 600px) {
  .dup-card-top { flex-direction: column; align-items: stretch; }
}
</style>
